import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { Progress } from '@backstage/core-components';
import { CompoundEntityRef } from '@backstage/catalog-model';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { techdocsStorageApiRef } from '@backstage/plugin-techdocs-react';

import {
  LATEST,
  VersionsManifest,
  decideTieredRedirect,
  stripTierSegment,
} from './versioning';
import { fetchVersionsManifest } from './versionsCache';
import { fetchTierConfig } from './tiersCache';
import { ResolvedTierConfig } from './tiers/types';

type RedirectState = {
  /** True once the tier-config lookup has settled (manifests may still load). */
  configLoaded: boolean;
  config: ResolvedTierConfig | null;
  /** True until every manifest the decision needs has settled. */
  loading: boolean;
  /** Per-tier manifests keyed by tier id (default tier included). */
  tierManifests: Record<string, VersionsManifest | null>;
  /** Entity-root manifest, fetched only for the pre-tier fallback. */
  rootManifest: VersionsManifest | null;
};

const INITIAL_STATE: RedirectState = {
  configLoaded: false,
  config: null,
  loading: true,
  tierManifests: {},
  rootManifest: null,
};

/**
 * Route-level wrapper for the standalone `/docs/:namespace/:kind/:name/*`
 * reader route (Story 9.3 AC 7-10; Story 6.3 AC 4-5, 7). It derives the entity
 * from `useParams()` — it runs *outside* the TechDocs reader context, so
 * `useTechDocsReaderPage()` is unavailable — resolves the entity's tier
 * configuration (cached per entity for the session) plus the relevant
 * `versions.json` manifests, and applies the combined tier × version
 * disambiguation rule *before* rendering the reader.
 *
 * The reader is passed in as `children` (the `<TechDocsReaderPage />` routable
 * extension) rather than constructed here, so the routing collector still finds
 * `rootDocsRouteRef` on the route element and the `viewTechDoc` external-route
 * binding keeps resolving. The `<TechDocsAddons>` remain a catch-all subroute
 * consumed by the reader via `useOutlet()`, unchanged by this wrapper.
 */
export function TechDocsReaderRedirect({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const params = useParams();
  const { search, hash } = useLocation();
  const catalogApi = useApi(catalogApiRef);
  const storageApi = useApi(techdocsStorageApiRef);
  const fetchApi = useApi(fetchApiRef);

  const namespace = params.namespace ?? '';
  const kind = params.kind ?? '';
  const name = params.name ?? '';
  const rest = params['*'] ?? '';
  const firstSegment = rest.replace(/^\/+/, '').split('/')[0] ?? '';

  const entityKey = `${kind}:${namespace}:${name}`.toLowerCase();

  const [state, setState] = useState<RedirectState>(INITIAL_STATE);

  useEffect(() => {
    let active = true;
    setState(INITIAL_STATE);
    const entityRef: CompoundEntityRef = { kind, namespace, name };
    (async () => {
      // Tier config first (AC 4): resolved for every entity via the 6.2
      // parser. Null means fail closed (unparseable annotation or catalog
      // failure) — Epic 9 behavior against the entity root.
      const config = await fetchTierConfig(catalogApi, entityRef);
      if (!active) {
        return;
      }
      setState(s => ({ ...s, configLoaded: true, config }));

      if (!config) {
        const rootManifest = await fetchVersionsManifest(
          storageApi,
          fetchApi,
          entityRef,
        );
        if (active) {
          setState(s => ({ ...s, loading: false, rootManifest }));
        }
        return;
      }

      // All manifests are entity-scoped, so one effect keyed by the entity
      // fetches everything the render-time decision can need — page
      // navigations within the reader never re-enter the loading state
      // (mirrors the pre-tier design, where the manifest lived in state and
      // the decision ran in render). Fetches are parallel and session-cached.
      const entries = await Promise.all(
        config.tiers.map(
          async tier =>
            [
              tier.id,
              await fetchVersionsManifest(
                storageApi,
                fetchApi,
                entityRef,
                tier.id,
              ),
            ] as const,
        ),
      );
      if (!active) {
        return;
      }
      const tierManifests = Object.fromEntries(entries);
      // No default-tier manifest ⇒ the entity hasn't republished under the
      // tier layout (AC 5) — the Epic 9 rule needs the entity-root manifest.
      const rootManifest = tierManifests[config.defaultTier]
        ? null
        : await fetchVersionsManifest(storageApi, fetchApi, entityRef);
      if (active) {
        setState(s => ({ ...s, loading: false, tierManifests, rootManifest }));
      }
    })();
    return () => {
      active = false;
    };
    // entityKey captures the only identity that changes the fetches; the APIs are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, storageApi]);

  const decision = state.loading
    ? null
    : decideTieredRedirect(rest, state.config, {
        defaultTier: state.config
          ? state.tierManifests[state.config.defaultTier] ?? null
          : null,
        seg1Tier: state.config
          ? state.tierManifests[
              stripTierSegment(
                rest,
                state.config.tiers.map(t => t.id),
              ).tier ?? ''
            ] ?? null
          : null,
        root: state.rootManifest,
      });

  // Canonical tiered fast path (AC 7): once the tier id is validated against
  // the resolved config, `<tier>/latest/…` can never redirect — the combined
  // rule applies case 2 (inner case 2) whenever the first segment is a
  // resolved tier id, migrated or not — so render the reader without awaiting
  // the manifest fetches.
  if (state.configLoaded && state.config) {
    const split = stripTierSegment(
      rest,
      state.config.tiers.map(t => t.id),
    );
    if (
      split.tier &&
      (split.rest.replace(/^\/+/, '').split('/')[0] ?? '') === LATEST
    ) {
      return <>{children}</>;
    }
  }

  // Legacy canonical fast path: `latest/…` renders immediately, exactly as
  // pre-tier (spinner-free for every legacy link and bookmark). For a
  // migrated tiered entity this URL is case 3 — but the entity-root mirror
  // serves the default tier's `latest/` (Story 6.1 amendment), i.e. the very
  // content the redirect targets, so no wrong content is flashed; the
  // Navigate below corrects the URL once the manifests resolve.
  if (firstSegment === LATEST) {
    if (decision?.redirect) {
      const to = `/docs/${namespace}/${kind}/${name}/${decision.to}${search}${hash}`;
      return <Navigate replace to={to} />;
    }
    return <>{children}</>;
  }

  // While the fetches are in flight, render a neutral pending state rather
  // than the reader — a slow fetch must never bounce a valid version URL, and
  // a bare URL must not flash un-canonicalized before redirecting.
  if (state.loading) {
    return <Progress />;
  }

  if (decision?.redirect) {
    const to = `/docs/${namespace}/${kind}/${name}/${decision.to}${search}${hash}`;
    return <Navigate replace to={to} />;
  }

  return <>{children}</>;
}
